'use client'

import { useState, useEffect } from 'react'

type CookiePreferences = {
  necessary: boolean
  analytics: boolean
  preferences: boolean
  marketing: boolean
}

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true, cannot be unchecked
    analytics: false,
    preferences: false,
    marketing: false,
  })

  useEffect(() => {
    // Check if consent has been given
    const consent = localStorage.getItem('cookieConsent')
    if (!consent) {
      setIsVisible(true)
    } else {
      // Load saved preferences
      try {
        const savedPrefs = JSON.parse(consent)
        setPreferences(savedPrefs)
      } catch {
        // If parsing fails, show consent dialog
        setIsVisible(true)
      }
    }

    // Listen for custom event to open consent dialog
    const handleOpenConsent = () => {
      setIsVisible(true)
    }
    window.addEventListener('openConsent', handleOpenConsent)

    return () => {
      window.removeEventListener('openConsent', handleOpenConsent)
    }
  }, [])

  const handleToggle = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return // Cannot toggle necessary
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      preferences: true,
      marketing: true,
    }
    setPreferences(allAccepted)
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted))
    setIsVisible(false)
  }

  const handleAcceptSelected = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences))
    setIsVisible(false)
  }

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      preferences: false,
      marketing: false,
    }
    setPreferences(onlyNecessary)
    localStorage.setItem('cookieConsent', JSON.stringify(onlyNecessary))
    setIsVisible(false)
  }

  if (!isVisible) return null

  return (
    <div className="cookie-consent-overlay" onClick={() => setIsVisible(false)}>
      <div className="cookie-consent-dialog" onClick={(e) => e.stopPropagation()}>
        <h2 className="cookie-consent-title">CONSENT SETTINGS</h2>
        <p className="cookie-consent-description">We use cookies to provide you with the best possible experience. They also allow us to analyze user statistics and improve our products and services.</p>

        <div className="cookie-consent-options">
          <label className="cookie-consent-option">
            <input type="checkbox" checked={preferences.necessary} disabled readOnly />
            <span>Necessary</span>
          </label>
          <label className="cookie-consent-option">
            <input type="checkbox" checked={preferences.analytics} onChange={() => handleToggle('analytics')} />
            <span>Analytics</span>
          </label>
          <label className="cookie-consent-option">
            <input type="checkbox" checked={preferences.preferences} onChange={() => handleToggle('preferences')} />
            <span>Preferences</span>
          </label>
          <label className="cookie-consent-option">
            <input type="checkbox" checked={preferences.marketing} onChange={() => handleToggle('marketing')} />
            <span>Marketing</span>
          </label>
        </div>

        <div className="cookie-consent-buttons">
          <button className="cookie-consent-btn cookie-consent-btn-accept" onClick={handleAcceptAll}>
            Accept All
          </button>
          <button className="cookie-consent-btn cookie-consent-btn-selected" onClick={handleAcceptSelected}>
            Accept Selected
          </button>
          <button className="cookie-consent-btn cookie-consent-btn-reject" onClick={handleRejectAll}>
            Reject All
          </button>
        </div>
      </div>
    </div>
  )
}
